import { apiClient, setAuthService } from '../api';
import { isTestingMode } from '../config';
import { jwtDecode } from 'jwt-decode';
import { storage } from '../utils/storage';
import { showSuccess, showError } from '../utils/toastHelper';
import { debugLog } from '../utils/debugLogger';

/**
 * Authentication Service
 * Contains all authentication-related API calls
 */

export const authService = {

  /**
   * Request password reset (sends OTP to email)
   * @param {string} email - Admin email address
   */
  requestPasswordReset: async (email) => {
    try {
      debugLog('🔐 Requesting password reset OTP for:', email);
      const response = await apiClient.post('/api/v1/admin/auth/forgot-password', { email });
      debugLog('🔐 Password reset OTP sent successfully');
      showSuccess('Password reset OTP sent to your email!');
      return response;
    } catch (error) {
      debugLog('Password reset request failed:', error);
      showError(error);
      throw error;
    }
  },

  /**
   * Reset password with OTP
   * @param {Object} resetData - Password reset data
   * @param {string} resetData.email - Admin email
   * @param {string} resetData.otp - OTP code
   * @param {string} resetData.new_password - New password
   * @param {string} resetData.confirm_password - Confirm password
   */
  resetPasswordWithOtp: async (resetData) => {
    try {
      debugLog('🔐 Resetting password with OTP for:', resetData.email);
      const response = await apiClient.post('/api/v1/admin/auth/reset-password', resetData);
      debugLog('🔐 Password reset successfully');
      showSuccess('Password reset successfully! You can now login.');
      return response;
    } catch (error) {
      debugLog('Password reset failed:', error);
      showError(error);
      throw error;
    }
  },

  // ====== ADMIN AUTHENTICATION ENDPOINTS ======

  /**
   * Admin login with email and password
   * P1 SECURITY FIX: Do NOT store user data until OTP is verified.
   * This prevents OTP bypass where user data presence indicates authentication.
   * @param {Object} credentials - Admin credentials
   * @param {string} credentials.email - Admin email
   * @param {string} credentials.password - Admin password
   */
  adminLogin: async (credentials) => {
    try {
      const response = await apiClient.post('/api/v1/admin/auth/login', {
        email: credentials.email,
        password: credentials.password
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // P1: Send/receive cookies
      });

      // P1 SECURITY FIX: Do NOT store user data until OTP verification completes
      // Only store temporary session data for password change flow
      if (response.password_change_required) {
        const tempSession = {
          email: credentials.email,
          user: response.user,
          password_change_required: response.password_change_required,
          current_password: credentials.password, // For password change flow
          temp_token: response.access_token || response.token, // Token for password change API
          timestamp: Date.now()
        };
        storage.setItem('temp_admin_session', JSON.stringify(tempSession));
        debugLog('🔐 Admin credentials verified - password change required');
      } else {
        // Password change NOT required - user must complete OTP verification
        // Do NOT store user data or tokens yet - wait for OTP verification
        debugLog('🔐 Admin credentials verified - OTP verification required');
      }

      return response;
    } catch (error) {
      // Enhanced error handling for account status issues
      if (error.message && error.message.toLowerCase().includes('admin account is not active')) {
        debugLog('🔐 Account status error detected - checking if account is PENDING');

        // Create a more helpful error message for PENDING accounts
        const enhancedError = new Error(
          'Account verification in progress. If this is a new account with PENDING status, please complete the password change process to activate your account.'
        );
        enhancedError.status = error.status;
        enhancedError.response = error.response;
        enhancedError.isAccountStatusError = true;
        enhancedError.originalMessage = error.message;

        debugLog('🔐 Enhanced error message for account status:', enhancedError.message);
        showError(enhancedError.message);
        throw enhancedError;
      }

      debugLog('Admin login error:', error);
      showError(error);
      throw error;
    }
  },

  /**
   * Admin login with OTP (Step 1: Request OTP)
   * @param {string} email - Admin email
   */
  adminLoginOtp: async (email) => {
    try {
      const response = await apiClient.post('/api/v1/admin/auth/login-otp', {
        email
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // P1: Send/receive cookies
      });
      showSuccess('OTP sent to your email successfully!');
      return response;
    } catch (error) {
      debugLog('Admin OTP request error:', error);
      showError(error);
      throw error;
    }
  },

  /**
   * Admin login with OTP (Step 2: Verify OTP)
   * P1 SECURITY: Tokens now stored in httpOnly cookies (XSS protection).
   * @param {Object} otpData - OTP verification data
   * @param {string} otpData.email - Admin email
   * @param {string} otpData.otp - OTP code
   */
  adminVerifyOtp: async (otpData) => {
    try {
      const response = await apiClient.post('/api/v1/admin/auth/verify-otp', {
        email: otpData.email,
        otp: otpData.otp
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // P1: Send/receive cookies
      });

      // P1 SECURITY: Tokens are now in httpOnly cookies (set by API)
      // Only store non-sensitive user data in localStorage
      if (response.user) {
        storage.setItem('admin_user', JSON.stringify(response.user));
      }

      // Store token expiration times if provided
      if (response.token_expires_at) {
        storage.setItem('token_expires_at', response.token_expires_at);
        debugLog('🔐 Stored access token expiration:', response.token_expires_at);
      }
      if (response.refresh_token_expires_at) {
        storage.setItem('refresh_token_expires_at', response.refresh_token_expires_at);
        debugLog('🔐 Stored refresh token expiration:', response.refresh_token_expires_at);
      }

      // Clear temporary session data
      storage.removeItem('temp_admin_session');
      storage.removeItem('temp_auth_data'); // Legacy cleanup

      debugLog('🔐 Admin OTP verification successful - tokens in httpOnly cookies');
      showSuccess('Login successful! Welcome back.');

      return response;
    } catch (error) {
      debugLog('Admin OTP verification error:', error);
      showError(error);
      throw error;
    }
  },

  /**
   * Change admin password
   * SECURITY: Uses temporary session token, not full authentication token
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.current_password - Current password
   * @param {string} passwordData.new_password - New password
   * @param {string} passwordData.confirm_password - Confirm new password
   * @param {string} accessToken - Optional temporary access token
   */
  adminChangePassword: async (passwordData, accessToken = null) => {
    try {
      // Use provided token or get from temporary session
      let token = accessToken;

      if (!token) {
        const tempSession = authService.getTempSession();
        if (tempSession?.temp_token) {
          token = tempSession.temp_token;
        } else {
          const error = new Error('No temporary session found. Please login again.');
          showError(error);
          throw error;
        }
      }

      const response = await apiClient.post('/api/v1/admin/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showSuccess('Password changed successfully!');
      return response;
    } catch (error) {
      debugLog('Admin password change error:', error);
      showError(error);
      throw error;
    }
  },

  /**
   * Update admin password by an authenticated admin
   * @param {string} newPassword - The new password
   */
  updateAdminPassword: async (newPassword) => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      throw new Error('Cannot update password on server side');
    }

    try {
      const token = storage.getItem('admin_access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      return await apiClient.post('/api/v1/admin/auth/update-password', {
        new_password: newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      debugLog('Admin update password error:', error);
      throw error;
    }
  },

  /* `adminCreateAccount` lived here and posted to
     `/api/v1/admin/auth/register`, which the API has never served —
     `app/api/admin/routes/auth.py` mounts ten routes and registration is not
     one of them. Its only caller was the create-password screen, so that
     screen 404'd on every submission.

     Removed rather than repointed. There is no self-registration in this
     console by design: an admin account is created by another admin under
     `/admins/create`, and the invited person only sets a password on an
     account that already exists. `resetPasswordWithOtp` below does that, and
     is what the create-password screen calls now. */

  /**
   * Refresh admin access token using refresh token
   * P1 SECURITY: Uses cookies instead of localStorage.
   * Enhanced with better retry logic and session persistence
   * @param {number} retryCount - Current retry attempt (for internal use)
   * @returns {Promise<boolean>} Whether refresh was successful
   */
  refreshAccessToken: async (retryCount = 0) => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return false;
    }

    const MAX_RETRIES = 3; // Increased from 2 to 3 for better reliability

    try {
      debugLog(`🔄 Attempting to refresh access token (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
      const response = await apiClient.post('/api/v1/admin/auth/refresh', {
        refresh_token: 'from_cookie'  // Placeholder, actual token from cookie
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // P1: Send/receive cookies
        suppressAuthErrorLogging: true, // Don't spam logs with auth errors during refresh
      });

      // Store token expiration times if provided by backend
      if (response.token_expires_at) {
        storage.setItem('token_expires_at', response.token_expires_at);
        debugLog('🔐 Token expiration time stored:', response.token_expires_at);
      }
      if (response.refresh_token_expires_at) {
        storage.setItem('refresh_token_expires_at', response.refresh_token_expires_at);
        debugLog('🔐 Refresh token expiration time stored:', response.refresh_token_expires_at);
      }

      // Update user data if provided (keeps permissions in sync)
      if (response.user) {
        storage.setItem('admin_user', JSON.stringify(response.user));
        debugLog('🔐 User data updated from refresh response');
      }

      // P1: New tokens are set in cookies by API (token rotation)
      debugLog('🔐 Token refresh successful - new tokens in httpOnly cookies');
      return true;
    } catch (error) {
      debugLog(`🔐 Token refresh failed (attempt ${retryCount + 1}):`, error);
      
      // If it's an auth error (401/403), don't retry - refresh token is invalid
      if (error.status === 401 || error.status === 403) {
        debugLog('🔐 Refresh token invalid or expired, logging out');
        authService.logout();
        return false;
      }
      
      // Retry on network errors or server errors (500+)
      const isRetryableError = !error.status || error.status >= 500;
      if (isRetryableError && retryCount < MAX_RETRIES) {
        const backoffDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s, 4s
        debugLog(`🔄 Retrying token refresh in ${backoffDelay}ms (${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return authService.refreshAccessToken(retryCount + 1);
      }
      
      // Clear user data if refresh fails after all retries
      debugLog('🔐 Token refresh failed after all retries, logging out');
      authService.logout();
      return false;
    }
  },

  /**
   * Validate current admin token by making a test API call
   * P1: Uses cookie-based authentication
   * @returns {Promise<boolean>} Whether the token is valid
   */
  validateToken: async () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return false;
    }

    // In testing mode, always return true if we have user data
    if (isTestingMode()) {
      const hasUser = !!storage.getItem('admin_user');
      debugLog('🧪 Testing Mode: Token validation bypassed, returning:', hasUser);
      return hasUser;
    }

    try {
      const hasUser = !!storage.getItem('admin_user');
      if (!hasUser) {
        return false;
      }

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        debugLog('🕐 Token validation timeout - assuming invalid token');
        controller.abort();
      }, 10000); // 10 second timeout

      try {
        // Make a lightweight API call to validate the token (cookie-based)
        await apiClient.get('/api/v1/admin/dashboard/overview', {}, {
          credentials: 'include',  // P1: Use cookies
          suppressAuthErrorLogging: true,
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        return true;
      } catch (error) {
        clearTimeout(timeoutId);

        // If it's an abort error (timeout), consider token invalid
        if (error.name === 'AbortError') {
          debugLog('🕐 Token validation timed out - clearing invalid session');
          storage.removeItem('admin_user');
          return false;
        }

        // If token is invalid, try to refresh it
        if (error.status === 401 || error.status === 403) {
          debugLog('🔐 Access token expired, attempting refresh');
          const refreshed = await authService.refreshAccessToken();
          return refreshed;
        }

        // Only log if it's not an expected auth error
        if (error.status !== 401 && error.status !== 403) {
          debugLog('Token validation failed:', error);
        }

        return false;
      }
    } catch (error) {
      debugLog('Token validation error:', error);
      return false;
    }
  },

  /**
   * Logout admin
   * P1 SECURITY: Calls API to clear httpOnly cookies, then clears local data.
   * Now handles both 200 (success) and 401 (expired session) gracefully.
   */
  logout: async () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // P1: Call API to clear httpOnly cookies
      // This should now return 200 even if session is expired
      await apiClient.post('/api/v1/admin/auth/logout', {}, {
        credentials: 'include',
        suppressAuthErrorLogging: true, // Don't log errors during logout
      });
      debugLog('🔐 API logout successful - cookies cleared');
    } catch (error) {
      // Any error is OK during logout - cookies should still be cleared by API
      debugLog('🔐 API logout completed (may have been expired session):', error);
    }

    // Clear local storage data
    storage.removeItem('admin_user');
    storage.removeItem('temp_admin_session');
    storage.removeItem('temp_auth_data'); // Legacy cleanup
    storage.removeItem('userEmail');
    storage.removeItem('token_expires_at');
    storage.removeItem('refresh_token_expires_at');

    debugLog('🔐 All admin data cleared');
  },

  /**
   * P2 SECURITY: Get CSRF token from cookie
   * 
   * CSRF tokens are stored in a non-httpOnly cookie so JavaScript can read them.
   * The cookie contains the SIGNED token which we send in the request header.
   * The server verifies the signature and validates the token.
   * 
   * @returns {string|null} CSRF token or null if not available
   */
  getCSRFToken: () => {
    if (typeof window === 'undefined') {
      return null;
    }

    // Read the CSRF token directly from the cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        debugLog('🔐 CSRF token found in cookie');
        return decodeURIComponent(value);
      }
    }

    debugLog('⚠️ CSRF token cookie not found! User may need to login again.');
    return null;
  },
  /**
   * P1 SECURITY: Check if admin is authenticated
   * Since tokens are in httpOnly cookies, we check for user data as a proxy.
   * @param {boolean} validateTokenOnServer - Whether to validate token on server
   * @returns {boolean|Promise<boolean>} Authentication status
   */
  isAuthenticated: (validateTokenOnServer = false) => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return false;
    }

    // In testing mode, always return true
    if (isTestingMode()) {
      const hasUser = !!storage.getItem('admin_user');
      debugLog('🧪 Testing Mode: Authentication check bypassed, has user:', hasUser);
      if (validateTokenOnServer) {
        return Promise.resolve(true);
      }
      return true;
    }

    // P1: Check if user data exists (proxy for cookie-based auth)
    // Cookies are httpOnly so we can't check them directly from JavaScript
    const hasUser = !!storage.getItem('admin_user');

    if (!hasUser) {
      return false;
    }

    // If we need server validation, return a promise
    if (validateTokenOnServer) {
      return authService.validateToken();
    }

    // Otherwise, just check user data presence
    return hasUser;
  },

  /**
   * Enhanced authentication check that validates token on server
   * Use this for critical authentication checks
   * @returns {Promise<boolean>} Authentication status after server validation
   */
  isAuthenticatedWithValidation: async () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return false;
    }

    // In testing mode, bypass server validation
    if (isTestingMode()) {
      const hasUser = !!storage.getItem('admin_user');
      debugLog('🧪 Testing Mode: Authentication with validation bypassed, has user:', hasUser);
      return true;
    }

    const hasUser = !!storage.getItem('admin_user');
    if (!hasUser) {
      return false;
    }

    return await authService.validateToken();
  },

  /**
   * Get current admin user
   */
  getCurrentUser: () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return null;
    }

    const userStr = storage.getItem('admin_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Get the current user's permissions array
   * DEPRECATED: Use RBAC system via ProtectedPage component instead
   * @returns {string[]} Array of permission strings
   */
  getUserPermissions: () => {
    // Return empty array - permissions now handled by RBAC
    return [];
  },

  /**
   * Check if the current user has a specific permission
   * DEPRECATED: Use RBAC system via ProtectedPage component instead
   * @param {string} permission - Permission to check
   * @returns {boolean} Always returns true - use RBAC instead
   */
  hasPermission: (permission) => {
    // Always return true - permissions now handled by RBAC system
    return true;
  },

  /**
   * Check if the current user has any of the listed permissions
   * DEPRECATED: Use RBAC system via ProtectedPage component instead
   * @param {string[]} permissions - Array of permission strings
   * @returns {boolean} Always returns true - use RBAC instead
   */
  hasAnyPermission: (permissions) => {
    // Always return true - permissions now handled by RBAC system
    return true;
  },

  /**
   * Update stored user data with fresh permissions from the /me endpoint.
   * Call this after a successful /me fetch to keep permissions in sync.
   * @param {Object} profileData - The admin profile data from /me
   */
  updateStoredPermissions: (profileData) => {
    const user = authService.getCurrentUser();
    if (user && profileData) {
      const updated = {
        ...user,
        permissions: profileData.permissions || [],
        role: profileData.role || user.role,
      };
      storage.setItem('admin_user', JSON.stringify(updated));
    }
  },

  /**
   * P1 DEPRECATED: Tokens are now in httpOnly cookies, not accessible from JavaScript
   * Kept for backward compatibility but returns null
   * @deprecated Use cookie-based authentication instead
   */
  isTokenExpired: function (token) {
    debugLog('⚠️ isTokenExpired is deprecated - tokens are in httpOnly cookies');
    return true; // Always return true since we can't access cookies
  },

  /**
   * P1 DEPRECATED: Tokens are now in httpOnly cookies, not accessible from JavaScript
   * Kept for backward compatibility but returns null
   * @deprecated Use cookie-based authentication instead
   */
  getToken: function () {
    debugLog('⚠️ getToken is deprecated - tokens are in httpOnly cookies');
    return null;
  },

  /**
   * P1 DEPRECATED: Tokens are now in httpOnly cookies, not accessible from JavaScript
   * Kept for backward compatibility but returns null
   * @deprecated Use cookie-based authentication instead
   */
  getTokenAsync: async function () {
    debugLog('⚠️ getTokenAsync is deprecated - tokens are in httpOnly cookies');
    return null;
  },

  /**
   * P1 DEPRECATED: Tokens are now in httpOnly cookies, not accessible from JavaScript
   * Kept for backward compatibility but returns null
   * @deprecated Use cookie-based authentication instead
   */
  getRefreshToken: () => {
    debugLog('⚠️ getRefreshToken is deprecated - tokens are in httpOnly cookies');
    return null;
  },

  /**
   * Clear invalid credentials and redirect to login
   * @param {Function} router - Next.js router instance (optional)
   */
  clearInvalidCredentials: (router = null) => {
    debugLog('Clearing invalid credentials');
    authService.logout();

    if (router) {
      router.replace('/login');
    } else if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },

  /**
   * SECURITY: Get temporary session data (used during password change flow)
   * @returns {Object|null} Temporary session data or null
   */
  getTempSession: () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const tempData = storage.getItem('temp_admin_session');
      if (!tempData) return null;

      const session = JSON.parse(tempData);

      // Check if session is expired (24 hour limit for security)
      const sessionAge = Date.now() - (session.timestamp || 0);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      if (sessionAge > maxAge) {
        debugLog('🔐 Temporary session expired, removing');
        storage.removeItem('temp_admin_session');
        return null;
      }

      return session;
    } catch (error) {
      debugLog('Error reading temporary session:', error);
      storage.removeItem('temp_admin_session');
      return null;
    }
  },

  /**
   * SECURITY: Check if user is in temporary session (partial authentication)
   * @returns {boolean} Whether user has temporary session but not full auth
   */
  hasTemporarySession: () => {
    const tempSession = authService.getTempSession();
    const isFullyAuthenticated = authService.isAuthenticated();
    return !!tempSession && !isFullyAuthenticated;
  },

  /**
   * SECURITY: Clear temporary session data
   */
  clearTempSession: () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return;
    }

    storage.removeItem('temp_admin_session');
    storage.removeItem('temp_auth_data'); // Legacy cleanup
    debugLog('🔐 Temporary session data cleared');
  },

  /**
   * SECURITY: Clear password from temporary session for security after password change
   */
  clearTempSessionPassword: () => {
    try {
      const tempSession = authService.getTempSession();
      if (tempSession && tempSession.current_password) {
        delete tempSession.current_password;
        storage.setItem('temp_admin_session', JSON.stringify(tempSession));
        debugLog('🔐 Current password cleared from temp session for security');
      }
    } catch (error) {
      debugLog('Error clearing password from temp session:', error);
    }
  },

  /**
   * SECURITY: Get authentication state summary for debugging
   * @returns {Object} Authentication state details
   */
  getAuthState: () => {
    // Check if running on client side
    if (typeof window === 'undefined') {
      return {
        isFullyAuthenticated: false,
        hasTemporarySession: false,
        hasAccessToken: false,
        hasRefreshToken: false,
        currentUser: null,
        tempSession: null,
        testingMode: isTestingMode(),
        serverSide: true
      };
    }

    return {
      isFullyAuthenticated: authService.isAuthenticated(),
      hasTemporarySession: authService.hasTemporarySession(),
      hasAccessToken: !!storage.getItem('admin_access_token'),
      hasRefreshToken: !!storage.getItem('admin_refresh_token'),
      currentUser: authService.getCurrentUser(),
      tempSession: authService.getTempSession(),
      testingMode: isTestingMode()
    };
  },

  /**
   * Set up mock authentication for testing mode
   * This creates the necessary tokens and user data for testing
   */
  setupMockAuth: () => {
    if (!isTestingMode()) {
      debugLog('setupMockAuth should only be called in testing mode');
      return;
    }

    debugLog('🧪 Testing Mode: Setting up mock authentication');
    const mockAuthData = {
      access_token: 'mock_admin_access_token_12345',
      refresh_token: 'mock_admin_refresh_token_67890',
      user: {
        id: 1,
        email: 'admin@inversepay.com',
        first_name: 'Admin',
        last_name: 'User',
        role: 'super_admin',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        last_login: new Date().toISOString()
      }
    };

    storage.setItem('admin_access_token', mockAuthData.access_token);
    storage.setItem('admin_refresh_token', mockAuthData.refresh_token);
    storage.setItem('admin_user', JSON.stringify(mockAuthData.user));

    debugLog('🧪 Testing Mode: Mock authentication tokens stored');
  }
};

// Register auth service with API client to enable automatic token refresh
setAuthService(authService);

export default authService; 